import { useState, useEffect, useCallback } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Buildings, User } from "@phosphor-icons/react";
import { toast } from "react-toastify";

interface BuildingLink {
  id: number;
  type: "contact" | "company";
  reason: string;
  buildingId: string;
  buildingAddress: string | null;
  buildingHousenr: string | null;
  contactId: number | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  companyId: number | null;
  companyName: string | null;
  companyEmail: string | null;
}

interface GroupedEntry {
  id: number;
  type: "contact" | "company";
  name: string;
  email: string | null;
  entityId: number;
  buildings: { buildingId: string; buildingAddress: string; reason: string }[];
}

const GebouwContacten = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [data, setData] = useState<GroupedEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  const fetchData = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/building-contacts-overview`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const rows: BuildingLink[] = await response.json();

        // Group by contact or company
        const map = new Map<string, GroupedEntry>();
        for (const row of rows) {
          const key = `${row.type}-${row.type === "contact" ? row.contactId : row.companyId}`;
          if (!map.has(key)) {
            map.set(key, {
              id: row.id,
              type: row.type,
              name:
                row.type === "contact"
                  ? `${row.contactFirstName ?? ""} ${row.contactLastName ?? ""}`.trim()
                  : (row.companyName ?? ""),
              email:
                row.type === "contact" ? row.contactEmail : row.companyEmail,
              entityId:
                row.type === "contact"
                  ? (row.contactId as number)
                  : (row.companyId as number),
              buildings: [],
            });
          }
          const entry = map.get(key)!;
          entry.buildings.push({
            buildingId: row.buildingId,
            buildingAddress:
              row.buildingAddress ||
              `${row.buildingId}${row.buildingHousenr ? ` ${row.buildingHousenr}` : ""}`,
            reason: row.reason,
          });
        }

        setData(Array.from(map.values()));
      } else {
        toast.error("Fout bij het ophalen van gebouwcontacten");
      }
    } catch (error) {
      console.error("Error fetching building contacts overview:", error);
      toast.error("Fout bij het ophalen van gebouwcontacten");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = data.filter((entry) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return (
      entry.name.toLowerCase().includes(q) ||
      (entry.email ?? "").toLowerCase().includes(q) ||
      entry.buildings.some(
        (b) =>
          b.buildingAddress.toLowerCase().includes(q) ||
          b.buildingId.toLowerCase().includes(q) ||
          b.reason.toLowerCase().includes(q)
      )
    );
  });

  const handleNavigate = (entry: GroupedEntry) => {
    if (entry.type === "contact") {
      navigate(`/contact/${entry.entityId}`);
    } else {
      navigate(`/company/${entry.entityId}`);
    }
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Gebouwcontacten | YallyHub</title>
      </Helmet>

      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-neutral-900">
              Gebouwcontacten
            </h1>
            {!isLoading && (
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-moss-primary px-2 text-xs font-semibold text-white">
                {filtered.length}
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Zoeken op naam, e-mail of gebouw..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full max-w-sm rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 outline-none focus:border-moss-primary focus:ring-1 focus:ring-moss-primary"
          />
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          {/* Header */}
          <div className="grid grid-cols-[2fr_2fr_3fr] border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-medium uppercase tracking-wide text-neutral-500">
            <span>Naam</span>
            <span>E-mail</span>
            <span>Gebouwen</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-neutral-500">
              Laden...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-neutral-500">
              Geen gebouwcontacten gevonden
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {filtered.map((entry) => (
                <div
                  key={`${entry.type}-${entry.entityId}`}
                  className="grid grid-cols-[2fr_2fr_3fr] items-start px-4 py-3 hover:bg-neutral-50"
                >
                  {/* Name */}
                  <div className="flex items-center gap-2">
                    {entry.type === "company" ? (
                      <Buildings
                        weight="duotone"
                        size={18}
                        className="shrink-0 text-neutral-400"
                      />
                    ) : (
                      <User
                        weight="duotone"
                        size={18}
                        className="shrink-0 text-neutral-400"
                      />
                    )}
                    <button
                      onClick={() => handleNavigate(entry)}
                      className="text-sm font-medium text-moss-primary hover:underline"
                    >
                      {entry.name || "–"}
                    </button>
                  </div>

                  {/* Email */}
                  <div className="text-sm text-neutral-600">
                    {entry.email ? (
                      <a
                        href={`mailto:${entry.email}`}
                        className="hover:underline"
                      >
                        {entry.email}
                      </a>
                    ) : (
                      <span className="text-neutral-400">–</span>
                    )}
                  </div>

                  {/* Buildings */}
                  <div className="flex flex-col gap-1.5">
                    {entry.buildings.map((b, idx) => (
                      <div key={idx} className="flex flex-col gap-0.5">
                        <button
                          onClick={() =>
                            navigate(`/building/${b.buildingId}`)
                          }
                          className="text-left text-sm font-medium text-moss-primary hover:underline"
                        >
                          {b.buildingAddress}
                        </button>
                        {b.reason && (
                          <span className="text-xs text-neutral-500">
                            {b.reason}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </HelmetProvider>
  );
};

export default GebouwContacten;
